// The trail IS the plan (player request 2026-06-12): dashes render the
// player's authoritative queued turns straight from the server snapshot —
// the exact route the simulation will take through the next junctions.
//
// Bounce-aware, continuing (player request, same day): hitting a square no
// longer ends the trail. The walk mirrors the simulation — direction flips at
// the bouncer (marked with a ring), the return leg is drawn on the same edge
// in the NEXT pass color, and the plan keeps unfolding from the junction
// behind. Queued turns survive bounces in the sim too.
//
// Self-overlap color coding: every repeated pass over an edge advances the
// color (orange → cyan → purple → rose) with a slight sideways offset, so a
// looping trail's end is always findable.
//
// Purely local: only the local player's queue is drawn.

import { Graphics, type Container } from 'pixi.js';
import { exitsFrom, type Bouncer, type HexGrid, type SnapshotPlayer } from '@evox/shared';
import { vertexToPx, nearestCopy, edgeParamToPx, type Px } from './geometry.js';

/** Pass colors: first time on an edge, second time, third, fourth+. */
const PASS_COLORS = [0xff7a2f, 0x19d3e0, 0x7a2fff, 0xff4fa3];
/** Sideways offset per extra pass, px — keeps overlapping colors visible. */
const PASS_OFFSET_PX = 5;
/** Stop the dash this far (param units of the edge) before a bouncer. */
const BOUNCE_MARGIN = 0.14;

let g: Graphics | null = null;

type Me = Pick<SnapshotPlayer, 'edgeId' | 'direction' | 't' | 'pendingTurns'>;

export function drawPathPreview(
  layer: Container,
  grid: HexGrid,
  me: Me | undefined,
  bouncers: Bouncer[],
  camera: Px,
): void {
  if (g === null) {
    g = new Graphics();
    layer.addChild(g);
  }
  g.clear();
  if (me === undefined || me.pendingTurns.length === 0) return;
  const edge = grid.edges[me.edgeId];
  if (edge === undefined) return;

  const bouncersByEdge = new Map<number, number[]>();
  for (const b of bouncers) {
    const list = bouncersByEdge.get(b.edgeId);
    if (list === undefined) bouncersByEdge.set(b.edgeId, [b.t]);
    else list.push(b.t);
  }

  /** Visits per edge within THIS trail — drives the pass color. */
  const visits = new Map<number, number>();
  const passOf = (edgeId: number): number => {
    const n = visits.get(edgeId) ?? 0;
    visits.set(edgeId, n + 1);
    return n;
  };

  // ── Current edge: dash ahead; on a bouncer, flip and keep walking ──────
  const forward = me.direction === 'forward';
  const behindVertex = forward ? edge.from : edge.to;
  const aheadVertex = forward ? edge.to : edge.from;
  let incomingEdge = me.edgeId;
  let frontierVertex: number;

  const firstPass = passOf(me.edgeId);
  const blockT = nearestBouncerAhead(bouncersByEdge.get(me.edgeId), me.t, forward);
  let endT = forward ? 0.98 : 0.02;
  if (blockT !== null) endT = forward ? blockT - BOUNCE_MARGIN : blockT + BOUNCE_MARGIN;
  const startT = forward ? Math.min(me.t + 0.18, endT) : Math.max(me.t - 0.18, endT);
  if (Math.abs(endT - startT) > 0.05) {
    drawDash(grid, edgeParamToPx(grid, me.edgeId, startT), edgeParamToPx(grid, me.edgeId, endT), camera, firstPass);
  }
  if (blockT !== null) {
    // Reversal: ring at the square, return leg in the next pass color,
    // then continue the plan from the junction behind the player.
    drawBounceMark(grid, edgeParamToPx(grid, me.edgeId, blockT), camera, firstPass);
    const returnPass = passOf(me.edgeId);
    const backT = forward ? 0.02 : 0.98;
    drawDash(grid, edgeParamToPx(grid, me.edgeId, endT), edgeParamToPx(grid, me.edgeId, backT), camera, returnPass);
    frontierVertex = behindVertex;
  } else {
    frontierVertex = aheadVertex;
  }

  // ── Walk the queue through successive junctions ────────────────────────
  for (const turn of me.pendingTurns) {
    const exits = exitsFrom(grid, frontierVertex, incomingEdge);
    const nextEdgeId = turn === 'left' ? exits.left : exits.right;
    const nextEdge = grid.edges[nextEdgeId]!;
    const enteringAtFrom = nextEdge.from === frontierVertex;
    const pass = passOf(nextEdgeId);
    const v = vertexToPx(grid.vertices[frontierVertex]!);
    const otherId = enteringAtFrom ? nextEdge.to : nextEdge.from;
    const o = nearestCopy(vertexToPx(grid.vertices[otherId]!), v, grid.dims);

    // Bouncer on this edge? Travel-relative position from the entry vertex.
    const ts = bouncersByEdge.get(nextEdgeId);
    let stopParam = 0.72;
    let bounceAbsT: number | null = null;
    if (ts !== undefined && ts.length > 0) {
      let best = Infinity;
      for (const t of ts) {
        const rel = enteringAtFrom ? t : 1 - t;
        if (rel < best) {
          best = rel;
          bounceAbsT = t;
        }
      }
      stopParam = Math.max(0.3, best - BOUNCE_MARGIN);
    }

    drawDash(
      grid,
      { x: v.x + (o.x - v.x) * 0.28, y: v.y + (o.y - v.y) * 0.28 },
      { x: v.x + (o.x - v.x) * stopParam, y: v.y + (o.y - v.y) * stopParam },
      camera,
      pass,
    );

    if (bounceAbsT !== null) {
      // Flip at the square: ring, return leg, and back to the SAME junction —
      // the next queued turn is consumed there, entering from this edge.
      drawBounceMark(grid, edgeParamToPx(grid, nextEdgeId, bounceAbsT), camera, pass);
      const returnPass = passOf(nextEdgeId);
      drawDash(
        grid,
        { x: v.x + (o.x - v.x) * stopParam, y: v.y + (o.y - v.y) * stopParam },
        { x: v.x + (o.x - v.x) * 0.18, y: v.y + (o.y - v.y) * 0.18 },
        camera,
        returnPass,
      );
      incomingEdge = nextEdgeId;
      // frontierVertex unchanged — we bounced back to it.
    } else {
      incomingEdge = nextEdgeId;
      frontierVertex = otherId;
    }
  }
}

function nearestBouncerAhead(ts: number[] | undefined, fromT: number, forward: boolean): number | null {
  if (ts === undefined) return null;
  let best: number | null = null;
  for (const t of ts) {
    if (forward ? t > fromT : t < fromT) {
      if (best === null || (forward ? t < best : t > best)) best = t;
    }
  }
  return best;
}

/** Ring around the square: "you will bounce here". */
function drawBounceMark(grid: HexGrid, atRaw: Px, camera: Px, pass: number): void {
  const at = nearestCopy(atRaw, camera, grid.dims);
  const color = PASS_COLORS[Math.min(pass, PASS_COLORS.length - 1)]!;
  g!.circle(at.x, at.y, 14).stroke({ width: 2.5, color, alpha: 0.85 });
  g!.circle(at.x, at.y, 19).stroke({ width: 1.5, color, alpha: 0.3 });
}

function drawDash(grid: HexGrid, aRaw: Px, bRaw: Px, camera: Px, pass: number): void {
  let a = nearestCopy(aRaw, camera, grid.dims);
  let b = nearestCopy(bRaw, a, grid.dims);
  const color = PASS_COLORS[Math.min(pass, PASS_COLORS.length - 1)]!;
  if (pass > 0) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    const side = pass % 2 === 1 ? 1 : -1;
    const off = PASS_OFFSET_PX * Math.ceil(pass / 2) * side;
    const ox = (-dy / len) * off;
    const oy = (dx / len) * off;
    a = { x: a.x + ox, y: a.y + oy };
    b = { x: b.x + ox, y: b.y + oy };
  }
  g!.moveTo(a.x, a.y).lineTo(b.x, b.y).stroke({ width: 9, color, alpha: 0.18, cap: 'round' });
  g!.moveTo(a.x, a.y).lineTo(b.x, b.y).stroke({ width: 4, color, alpha: 0.92, cap: 'round' });
}
